import { AvailableModules } from 'generated/featureDefinitions'

import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import { Config as NpsConfig, VisibilityConfigKey } from 'modules/nps/lib/types'
import { getNpsVisibility } from 'modules/nps/lib/visibility'

import { useFetchShouldShowNps } from './useFetchShouldShowNps'

type WidgetConfig = NpsConfig & {
  shouldShow: boolean
  productId: VisibilityConfigKey
}

const DEFAULT_CONFIG: WidgetConfig = {
  icon: 'schedule',
  variant: 'radical',
  productName: 'Factorial',
  productId: 'factorial',
  shouldShow: false,
  routes: [],
}

export const useNpsVisibilityForPath = (currentPath: string): WidgetConfig => {
  const { navigation } = useNavigation()
  const visibilityConfig = getNpsVisibility(navigation)
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const matchingKey = Object.entries(visibilityConfig).find(([k, v]) => {
    if (v.routes.some((path) => currentPath.startsWith(path))) return [k, v]

    return
  })?.[0] as VisibilityConfigKey | undefined

  const productConfig = matchingKey ? visibilityConfig[matchingKey] : undefined

  const { loading: loadingVisibility, shouldShow } = useFetchShouldShowNps(
    matchingKey || DEFAULT_CONFIG.productId
  )

  const onboardingHubKey = 'onboarding_hub'

  const isAcquired =
    useCheckModuleAcquired(matchingKey || DEFAULT_CONFIG.productId) ||
    matchingKey === 'factorial' ||
    matchingKey === onboardingHubKey

  if (!matchingKey || !productConfig) return DEFAULT_CONFIG

  return {
    ...productConfig,
    productId: matchingKey,
    shouldShow: loadingVisibility ? false : !!shouldShow && isAcquired,
  }
}

export const useNpsVisibilityAllProducts = (): WidgetConfig[] => {
  const { navigation } = useNavigation()
  const { moduleAvailabilities } = useModuleAvailabilities()
  const visibilityConfig = getNpsVisibility(navigation)

  return Object.entries(visibilityConfig)
    .map(([matchingKey, productConfig]) => {
      const isAcquired =
        isModuleAvailabilityEnabled(
          moduleAvailabilities,
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          (matchingKey || DEFAULT_CONFIG.productId) as AvailableModules
        ) || matchingKey === 'factorial'

      if (!matchingKey) return DEFAULT_CONFIG

      return {
        ...productConfig,
        productId: matchingKey,
        shouldShow: isAcquired,
      }
    })
    .filter(Boolean)
}
